import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@services/security';
import { CompanyService } from '@app/company/services/company.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ProfileService } from '@ui/profiles-company';
import { isEmpty } from 'lodash';
import { Observable, tap } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-service-permissions',
  templateUrl: './service-permissions.component.html',
  styleUrls: ['./service-permissions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServicePermissionsComponent implements OnInit {
    profile;
    public isSubcheckDisabled;
    public isCommonLaw: boolean | null = null;
    label: string;
    servicePermissions;
    label$: Observable<string | null>;
    company;

    constructor(
        private route: ActivatedRoute,
        public authService: AuthService,
        private companyService: CompanyService,
        private profileService: ProfileService,
        private change: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.initPermissions();
    }

    initPermissions() {
        this.label$ = this.route.paramMap.pipe(
            map(params => params.get('label') ?? 'general')
        );
        this.route.params
            .pipe(
                untilDestroyed(this),
                tap(params => {
                    this.label = params['label'];
                    this.profile = this.route.snapshot.parent?.data.profile || {};
                    this.servicePermissions = { ...(this.profile.permissions ?? {}) };

                    this.profileService.labels.forEach(label => {
                        if (isEmpty(this.servicePermissions[label])) {
                            this.servicePermissions[label] =
                                this.profileService.profile.permissions[label];
                        }
                    });
                }),
                switchMap(() => this.companyService.getCompany(this.profile.companyId))
            )
            .subscribe(company => {
                this.company = company;
                this.isCommonLaw = company?.inclusionTypes?.includes('commonLaw') ?? false;
                this.change.markForCheck();
            });
            this.setIsSubcheckDisabled();
    }

    public disablePermissions(event) {
        this.setIsSubcheckDisabled();

        Object.keys(this.servicePermissions[this.label]).forEach(
            (permission) => {
                if (permission === event.target.id) return;

                this.servicePermissions[this.label][permission] = false;
            },
        );
    }

    private setIsSubcheckDisabled() {
        if (this.label === 'general') return;
        this.isSubcheckDisabled =
            (this.authService.isUser && this.profile.isLocked) ||
            !this.servicePermissions[this.label].canActivateView;
    }

    disableConditionalPermission(permission: string) {
        this.servicePermissions[this.label].canBeReferent = false;
        switch (permission) {
            case 'canOnlyReadBills':
                this.servicePermissions[this.label].canBill = false;
                break;
            case 'canViewMedicalAlerts':
                this.servicePermissions[this.label].canManageMedicalAlerts = false;
                break;
            case 'canConsultDocumentsAndPrescriptions':
                this.servicePermissions[this.label].canImportDocumentOnPatientRecord = false;
                this.servicePermissions[this.label].canGeneratePrescription =
                    false;
                this.servicePermissions[this.label].canIncludePatient = false;
                this.servicePermissions[this.label].canReincludePatient = false;
                break;
            case 'canEditAdministrativeInfos':
            case 'canEditMedicalInfos':
            case 'canGeneratePrescription':
                this.servicePermissions[this.label].canIncludePatient = false;
                this.servicePermissions[this.label].canReincludePatient = false;
                break;
            case 'canViewTherapyCoaching':
                this.servicePermissions[this.label].canAddTherapyCoachingSession =
                    false;
                break;
            case 'canIncludePatient':
            case 'canReincludePatient':
            case 'canStopPatient':
            case 'canReportEvent':
            case 'canImportDocumentOnPatientRecord':
            case 'canViewMedicalEventsAndComment':
            case 'canViewTechnicalEventsAndComment':
            case 'canAddTherapyCoachingSession':
                break;
            default:
                break;
        }
    }

    activateConditionalPermission(permission: string) {
        switch (permission) {
            case 'canBill':
                this.servicePermissions[this.label].canOnlyReadBills = true;
                break;
            case 'canIncludePatient':
                this.servicePermissions[this.label].canEditAdministrativeInfos = true;
                this.servicePermissions[this.label].canEditMedicalInfos = true;
                this.servicePermissions[this.label].canGeneratePrescription = true;
                this.servicePermissions[this.label].canConsultDocumentsAndPrescriptions = true;
                this.servicePermissions[this.label].canReincludePatient = this.isCommonLaw;
                break;
            case 'canReincludePatient':
                this.servicePermissions[this.label].canEditAdministrativeInfos = true;
                this.servicePermissions[this.label].canEditMedicalInfos = true;
                this.servicePermissions[this.label].canGeneratePrescription = true;
                this.servicePermissions[this.label].canConsultDocumentsAndPrescriptions = true;
                break;
            case 'canGeneratePrescription':
            case 'canImportDocumentOnPatientRecord':
                this.servicePermissions[this.label].canConsultDocumentsAndPrescriptions = true;
                break;
            case 'canAddTherapyCoachingSession':
                this.servicePermissions[this.label].canViewTherapyCoaching = true;
                break;
            case 'canManageMedicalAlerts':
                this.servicePermissions[this.label].canViewMedicalEventsAndComment = true;
                this.servicePermissions[this.label].canViewMedicalAlerts = true;
                break;
            case 'canViewMedicalAlerts':
                this.servicePermissions[this.label].canViewMedicalEventsAndComment = true;
                break;
            case 'canReportEvent':
                this.servicePermissions[this.label].canViewMedicalEventsAndComment = true;
                break;
            case 'canConsultDocumentsAndPrescriptions':
            case 'canStopPatient':
            case 'canEditAdministrativeInfos':
            case 'canEditMedicalInfos':
            case 'canViewMedicalEventsAndComment':
            case 'canViewTechnicalEventsAndComment':
            case 'canViewTherapyCoaching':
                break;
            default:
                break;
        }
    }

    changeConditionalPermission(event, permission: string) {
        if (event.target.checked) {
            this.activateConditionalPermission(permission);
        } else {
            this.disableConditionalPermission(permission);
        }
    }

    activateReferentConditionalPermissions() {
        Object.keys(this.servicePermissions[this.label]).forEach(
            (permission) => {
                this.servicePermissions[this.label][permission] = true;
            },
        );
        this.servicePermissions[this.label].canReincludePatient = this.isCommonLaw;
    }
}
